import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Mic, Download, Check, Mail, MapPin } from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import {
  BRAND, PILLARS, GEOGRAPHY, OUTCOMES, FRAMEWORKS, INSIGHTS,
  TECH_STACK, CHALLENGES, WHY, ASSESSMENT, VIDEOS, RESOURCES,
  SERVICES, INDUSTRIES,
} from "@/data/content";
import { IMG, VIDEO_THUMBS } from "@/data/images";

const PORTRAIT = IMG.portrait;
const BOARDROOM = IMG.boardroom;

function SectionHead({ overline, lines, className = "" }) {
  return (
    <div className={className}>
      <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">{overline}</p>
      <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-6xl">
        <KineticHeadingInView lines={lines} />
      </h2>
    </div>
  );
}

/* ---------------------------------- HERO ---------------------------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-navy text-white grain-overlay">
      <motion.div aria-hidden className="pointer-events-none absolute -right-40 top-0 h-[700px] w-[700px] rounded-full bg-royal/25 blur-[150px]"
        animate={{ x: [0, -40, 0], y: [0, 40, 0], scale: [1, 1.08, 1] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div aria-hidden className="pointer-events-none absolute -left-40 bottom-0 h-[520px] w-[520px] rounded-full bg-sky/10 blur-[130px]"
        animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.12, 1] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-6 pb-20 pt-32 md:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <motion.div style={{ y }} className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
              className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">
              Fractional CIO &amp; Executive Technology Advisor
            </motion.p>
            <h1 className="mt-6 font-heading text-[13vw] font-light leading-[0.92] tracking-tighter sm:text-7xl lg:text-[6rem]">
              <KineticHeading lines={["Executive technology", "leadership for"]} lineClassName="font-light" />
              <KineticHeading lines={["growing businesses."]} lineClassName="font-black text-sky" />
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 max-w-xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
              Growing businesses reach a point where technology becomes critical to expansion,
              operational excellence and long-term competitiveness. Yet many do not need, or cannot
              justify, a full-time Chief Information Officer. As a Fractional CIO and Executive
              Technology Advisor, Aftab Azad works with owners, CEOs, managing directors and executive
              teams to make better technology decisions, strengthen governance and support sustainable
              growth across India, the UAE and the Gulf Region.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="hero-book-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
                Book Executive Strategy Session
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link to="/services/executive-advisory" data-testid="hero-outcomes-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
                Explore Advisory Services
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.9 }}
              data-testid="hero-trust" className="mt-14 border-t border-white/10 pt-6">
              <p className="font-body text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">Trusted at the highest level</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                {PILLARS.map((p, i) => (
                  <span key={p} className="flex items-center gap-3">
                    <span className="font-heading text-sm font-semibold text-slate-200">{p}</span>
                    {i < PILLARS.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/60" />}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-2">
                {GEOGRAPHY.map((g, i) => (
                  <span key={g} className="flex items-center gap-2.5">
                    <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{g}</span>
                    {i < GEOGRAPHY.length - 1 && <span className="h-0.5 w-0.5 rounded-full bg-slate-500" />}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: imgY }} className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 1 }}
              className="relative ml-auto max-w-md">
              <img
                src={PORTRAIT}
                alt="Aftab Azad, Executive Advisor & Fractional CIO"
                className="w-full object-contain drop-shadow-[0_35px_80px_rgba(0,0,0,0.55)]"
              />
              <p className="mt-6 text-center font-signature text-5xl leading-none text-white">Aftab Azad</p>
              <p className="mt-2 text-center font-heading text-sm font-semibold uppercase tracking-[0.2em] text-white">
                Executive Advisor | Fractional CIO
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- TECH MARQUEE ----------------------------- */
function TechMarquee() {
  return (
    <section data-testid="tech-marquee" className="border-b border-slate-200 bg-white py-12">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <p className="text-center font-body text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">
          Advising on the platforms shaping the enterprise
        </p>
        <div className="mt-8">
          <Marquee autoFill speed={35} gradient gradientColor="#FFFFFF" gradientWidth={90} pauseOnHover>
            {TECH_STACK.map((t) => {
              const Icon = t.icon;
              return (
                <div key={t.name} className="mx-10 flex items-center gap-3 opacity-70 transition-opacity hover:opacity-100">
                  {t.slug ? (
                    <img
                      src={`https://cdn.simpleicons.org/${t.slug}/475569`}
                      alt={`${t.name} logo`}
                      className="h-7 w-7 object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <Icon className="h-6 w-6 text-slate-600" strokeWidth={1.5} />
                  )}
                  <span className="whitespace-nowrap font-heading text-lg font-bold tracking-wide text-slate-600">{t.name}</span>
                </div>
              );
            })}
          </Marquee>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- TRUST BAR ------------------------------- */

/* ---------------------------- BUSINESS CHALLENGES ------------------------- */
function BusinessChallenges() {
  return (
    <section data-testid="business-challenges" className="bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHead overline="The questions on the table" lines={["What keeps leaders", "awake at night."]} className="max-w-2xl" />
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {CHALLENGES.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.q} delay={(i % 2) * 0.08}>
                <div className="flex h-full gap-6 rounded-[1.5rem] border border-slate-200 bg-white p-8">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold leading-snug tracking-tight text-navy">{c.q}</h3>
                    <p className="mt-3 font-body leading-relaxed text-slate-600">{c.a}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-14 max-w-3xl font-body text-lg leading-relaxed text-slate-600">
          These are the decisions Aftab Azad is trusted to advise on, bringing an independent,
          board-level perspective to the technology choices that shape enterprise value.
        </p>
      </div>
    </section>
  );
}

/* --------------------------- EXECUTIVE ADVISORY --------------------------- */
function ExecutiveAdvisory() {
  return (
    <section data-testid="executive-advisory" className="bg-white py-24 md:py-32">
      <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal className="overflow-hidden rounded-[2rem] border border-slate-200">
            <img src={BOARDROOM} alt="Executive boardroom" className="h-[520px] w-full object-cover" />
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why leaders work with Aftab</p>
          <h2 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
            <KineticHeadingInView lines={["Business first.", "Technology second."]} />
          </h2>
          <div className="mt-6 space-y-5 font-body text-lg leading-relaxed text-slate-600">
            <p>
              Aftab Azad does not begin with technology. He begins with the business, the
              strategy, the economics and the outcomes the board is accountable for, and
              works back to the technology decisions that serve them.
            </p>
            <p>
              That discipline is the difference. Technology exists to enable growth, not the
              other way around. Every recommendation is anchored to a measurable business
              result and judged by a single question: is the enterprise stronger for it?
            </p>
          </div>
          <Link to="/about" data-testid="advisory-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body font-semibold text-white transition-colors hover:bg-navy">
            More about Aftab
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- SERVICES ------------------------------- */
function Services() {
  return (
    <section data-testid="services-section" className="bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHead overline="Advisory services" lines={["Six ways I create", "enterprise value."]} className="max-w-2xl" />
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8 transition-colors duration-300 hover:border-royal">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy/5 text-navy transition-colors group-hover:bg-navy group-hover:text-white">
                    <Icon className="h-7 w-7" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-3 flex-1 font-body leading-relaxed text-slate-600">{s.lead}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold text-royal">
                    Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- INDUSTRIES ------------------------------ */
function IndustriesPreview() {
  return (
    <section data-testid="industries-preview" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead overline="Industries" lines={["Trusted across", "complex sectors."]} />
            <Link to="/industries" data-testid="industries-view-all" className="mt-8 inline-flex items-center gap-2 font-body text-sm font-semibold text-royal hover:text-navy">
              View all industries <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.icon;
              return (
                <div key={ind.name} className="bg-white p-7 transition-colors hover:bg-surface">
                  <Icon className="h-8 w-8 text-navy" strokeWidth={1.5} />
                  <h3 className="mt-4 font-heading text-lg font-bold text-navy">{ind.name}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-slate-500">{ind.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- BUSINESS OUTCOMES -------------------------- */
function BusinessOutcomes() {
  return (
    <section data-testid="business-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
      <div className="pointer-events-none absolute inset-0">
        <img src={IMG.architectureDubai} alt="" aria-hidden className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/85 to-navy" />
      </div>
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="max-w-2xl">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
          <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-6xl">
            <KineticHeadingInView lines={["The outcomes that", "matter to the board."]} />
          </h2>
          <p className="mt-6 font-body text-lg leading-relaxed text-slate-300">
            Advisory is measured by the results it produces. This is the value leadership
            teams engage Aftab to create, and the frameworks that deliver it.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
          {OUTCOMES.map((o) => (
            <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
              <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
              <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {FRAMEWORKS.map((f, i) => (
            <Reveal key={f.slug} delay={i * 0.08}>
              <Link to="/case-studies" data-testid={`framework-${f.slug}`}
                className="group flex h-full flex-col rounded-[1.5rem] border border-white/10 bg-white/5 p-8 backdrop-blur transition-colors hover:border-sky/50">
                <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-sky">{f.area}</span>
                <h3 className="mt-5 font-heading text-2xl font-semibold leading-snug">{f.title}</h3>
                <p className="mt-4 flex-1 font-body text-sm leading-relaxed text-slate-300">{f.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold text-sky">
                  Explore the framework <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- WHY WORK WITH ME ---------------------------- */
function WhyWorkWithMe() {
  return (
    <section data-testid="why-work-with-me" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHead overline="Why work with me" lines={["The difference in", "the room."]} className="max-w-2xl" />
        <div className="mt-16 grid gap-x-12 gap-y-14 md:grid-cols-2">
          {WHY.map((w) => {
            const Icon = w.icon;
            return (
              <Reveal key={w.title} className="flex gap-6 border-t border-slate-200 pt-8">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                  <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- INSIGHTS ------------------------------- */
function InsightsPreview() {
  return (
    <section data-testid="insights-preview" className="bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Insights" lines={["Thinking for", "decision-makers."]} />
          <Link to="/insights" data-testid="insights-view-all" className="inline-flex items-center gap-2 font-body text-sm font-semibold text-royal hover:text-navy">
            All insights <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-x-8 gap-y-10 md:grid-cols-2">
          {INSIGHTS.slice(0, 4).map((a, i) => (
            <Reveal key={a.slug} delay={(i % 2) * 0.08}>
              <Link to="/insights" data-testid={`insight-preview-${a.slug}`} className="group block border-t border-slate-200 pt-6">
                <div className="flex items-center gap-3 font-body text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <span className="text-royal">{a.category}</span> · <span>{a.read}</span>
                </div>
                <h3 className="mt-3 font-heading text-2xl font-bold leading-snug tracking-tight text-navy transition-colors group-hover:text-royal">{a.title}</h3>
                <p className="mt-3 font-body leading-relaxed text-slate-600">{a.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- SPEAKING ------------------------------- */
function SpeakingPreview() {
  return (
    <section data-testid="videos-preview" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Speaking" lines={["Ideas worth the", "boardroom's time."]} />
          <Link to="/videos" data-testid="videos-view-all" className="inline-flex items-center gap-2 font-body text-sm font-semibold text-royal hover:text-navy">
            All speaking topics <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {VIDEOS.slice(0, 3).map((v, i) => (
            <Reveal key={v.id} delay={(i % 3) * 0.08}>
              <Link to="/videos" data-testid={`video-preview-${v.id}`} className="group block">
                <div className="relative flex aspect-video items-end overflow-hidden rounded-[1.5rem] bg-navy p-6">
                  <img src={VIDEO_THUMBS[v.id]} alt={v.title} className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
                  <span className="relative inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.15em] text-white backdrop-blur">
                    <Mic className="h-3.5 w-3.5" strokeWidth={2} /> Keynote &amp; panel
                  </span>
                </div>
                <span className="mt-4 block font-body text-xs font-bold uppercase tracking-[0.2em] text-royal">{v.topic}</span>
                <h3 className="mt-2 font-heading text-lg font-bold leading-snug tracking-tight text-navy transition-colors group-hover:text-royal">{v.title}</h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- RESOURCES ------------------------------- */
function ResourcesPreview() {
  return (
    <section data-testid="resources-preview" className="bg-surface py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Briefings" lines={["Tools built for", "the boardroom."]} />
          <Link to="/resources" data-testid="resources-view-all" className="inline-flex items-center gap-2 font-body text-sm font-semibold text-royal hover:text-navy">
            All briefings <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {RESOURCES.slice(0, 4).map((r, i) => (
            <Reveal key={r.title} delay={(i % 2) * 0.08}>
              <Link to="/resources" data-testid={`resource-preview-${i}`}
                className="group flex items-start gap-6 rounded-[1.5rem] border border-slate-200 bg-white p-8 transition-colors hover:border-royal">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors group-hover:bg-navy group-hover:text-white">
                  <Download className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <div>
                  <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-royal">{r.type}</span>
                  <h3 className="mt-2 font-heading text-xl font-bold leading-snug tracking-tight text-navy">{r.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{r.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- EXECUTIVE ASSESSMENT ------------------------- */
function ExecutiveAssessment() {
  return (
    <section data-testid="executive-assessment" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
      <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Complimentary</p>
          <h2 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight md:text-5xl">
            <KineticHeadingInView lines={["The Executive", "Technology Assessment."]} />
          </h2>
          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-slate-300">{ASSESSMENT.intro}</p>
          <Link to="/book" data-testid="assessment-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body font-semibold text-white transition-colors hover:bg-sky hover:text-navy">
            Request your assessment
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <div className="rounded-[1.75rem] border border-white/10 bg-white/5 p-8 md:p-10">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-slate-400">What you receive</p>
            <ul className="mt-6 space-y-4">
              {ASSESSMENT.points.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="font-body text-slate-200">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ CREDIBILITY ------------------------------ */
function Credibility() {
  const served = ["Chairmen & Boards", "CEOs & Managing Directors", "Founders & Business Owners", "Private Equity & Investors"];
  return (
    <section data-testid="credibility-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHead overline="Trusted counsel" lines={["Brought in when the", "answer has to be right."]} />
            <p className="mt-8 max-w-xl font-body text-lg leading-relaxed text-slate-600">
              CEOs, chairmen, boards and investors bring Aftab in for the decisions where the cost
              of being wrong is measured in years, not quarters. The value is judgement, independent,
              direct and accountable to the result.
            </p>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200">
              {served.map((s) => (
                <div key={s} className="flex items-center gap-4 bg-white px-7 py-6">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full bg-royal" />
                  <span className="font-heading text-lg font-semibold tracking-tight text-navy">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- CONTACT -------------------------------- */
function ContactBand() {
  return (
    <section data-testid="home-contact" className="relative overflow-hidden bg-surface py-24 md:py-32">
      <div className="pointer-events-none absolute right-0 top-1/2 h-[500px] w-[500px] -translate-y-1/2 translate-x-1/3 rounded-full bg-sky/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Contact</p>
          <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-6xl">
            <KineticHeadingInView lines={["Make technology a", "strategic advantage."]} />
          </h2>
          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-slate-600">
            Whether you are planning a digital transformation, evaluating AI opportunities or
            seeking executive technology leadership, let&rsquo;s discuss how technology can become
            a strategic advantage for your business.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="home-contact-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body font-semibold text-white transition-colors hover:bg-navy">
              Schedule Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/contact" data-testid="home-contact-message"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-8 py-4 font-body font-medium text-navy transition-colors hover:border-royal hover:text-royal">
              Send a message
            </Link>
          </div>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <div className="space-y-4">
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-colors hover:border-royal">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy"><Mail className="h-5 w-5" strokeWidth={1.5} /></div>
              <div>
                <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Email</p>
                <p className="font-heading font-semibold text-navy">{BRAND.email}</p>
              </div>
            </a>
            <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy"><MapPin className="h-5 w-5" strokeWidth={1.5} /></div>
              <div>
                <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Location</p>
                <p className="font-heading font-semibold text-navy">{BRAND.location}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title={null}
        description="Aftab Azad is an Executive Technology Advisor and Fractional CIO to CEOs, boards, growing businesses and family-owned enterprises. Board-level technology strategy, digital transformation and AI advisory across India, the UAE and the Gulf Region."
        path="/"
        keywords={["Executive Technology Advisor", "Fractional CIO", "Fractional CIO India", "Fractional CIO UAE", "Fractional CIO Gulf", "Virtual CIO", "Mid-Market CIO", "Family-Owned Business Advisor", "Digital Transformation Advisor", "Technology Strategy Consultant", "Board Technology Advisor", "AI Strategy Consultant", "Enterprise Technology Leadership"]}
      />
      <Hero />
      <TechMarquee />
      <BusinessChallenges />
      <ExecutiveAdvisory />
      <Services />
      <IndustriesPreview />
      <BusinessOutcomes />
      <WhyWorkWithMe />
      <InsightsPreview />
      <SpeakingPreview />
      <ResourcesPreview />
      <ExecutiveAssessment />
      <Credibility />
      <ContactBand />
    </>
  );
}
